import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEO } from '../lib/seo';
import { fetchCmsList } from '../lib/cms';
import { GalleryLightbox } from '../components/GalleryLightbox';
import { ContactForm } from '../components/ContactForm';

export function ServiceDetail({ settings }) {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allServices, setAllServices] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [servicesList, galleriesList] = await Promise.all([
        fetchCmsList('services'),
        fetchCmsList('gallery')
      ]);

      setAllServices(servicesList);

      // Find service by matching slug or fallback alias
      const currentService = servicesList.find((s) => {
        const sSlug = s.slug?.da || s.slug || '';
        return (
          sSlug === slug ||
          s.id === slug ||
          (slug === 'komplet-fodbehandling' && s.title.includes('Komplet')) ||
          (slug === 'abonnement' && s.title.includes('Abonnement')) ||
          (slug === 'laserbehandling' && s.title.includes('Laser')) ||
          (slug === 'produkter-og-cremer' && (s.title.includes('Cremer') || s.title.includes('Produkter')))
        );
      });

      if (currentService) {
        setService(currentService);
        const gKey = currentService.gallery_key || currentService.gallery_slug || 'fodbehandling-hjemme';
        const matchedGallery = galleriesList.find((g) => g.gallery_key === gKey || g.slug?.da === gKey);
        setGallery(matchedGallery || null);
      }
      setLoading(false);
    }
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20 bg-canvas">
        <div className="text-center space-y-3">
          <i className="fa-solid fa-circle-notch fa-spin text-3xl text-brand-accent" aria-hidden="true" />
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Henter behandling...</p>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-[60vh] max-w-2xl mx-auto px-4 py-20 text-center space-y-6 bg-canvas">
        <h1 className="font-serif text-3xl font-bold text-brand-charcoal">Ydelse ikke fundet</h1>
        <p className="text-brand-muted text-sm">
          Vi kunne desværre ikke finde den ønskede behandling.
        </p>
        <Link
          to="/ydelser"
          className="inline-flex items-center gap-2 bg-brand-charcoal text-canvas font-bold px-6 py-3 rounded-full text-xs uppercase tracking-wider"
        >
          <span>Se alle behandlinger</span>
        </Link>
      </div>
    );
  }

  const phone = settings?.phone || "82 82 10 10";

  return (
    <>
      <SEO 
        title={`${service.title} | Fodtid`}
        description={service.short_description || `Læs om ${service.title} hos Fodtid – professionel mobil fodpleje i eget hjem.`}
        image={service.image?.[0]}
      />

      {/* Top Banner */}
      <section className="bg-canvas py-14 sm:py-18 border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          <div className="flex items-center gap-2 text-xs font-semibold text-brand-muted mb-6">
            <Link to="/" className="hover:text-brand-charcoal">Forside</Link>
            <span>/</span>
            <Link to="/ydelser" className="hover:text-brand-charcoal">Ydelser</Link>
            <span>/</span>
            <span className="text-brand-charcoal">{service.title}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-3.5">
              {service.price && (
                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-surface text-brand-charcoal text-xs font-bold border border-brand-border">
                  <i className="fa-solid fa-tag text-brand-accent" aria-hidden="true" />
                  <span>{service.price}</span>
                </div>
              )}
              <h1 className="font-serif text-3xl sm:text-4xl font-bold text-brand-charcoal leading-tight">
                {service.title}
              </h1>
              <p className="text-brand-muted text-sm sm:text-base leading-relaxed max-w-2xl">
                {service.short_description}
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end">
              <a
                href="#booking"
                className="inline-flex items-center justify-center gap-2 bg-brand-charcoal hover:bg-black text-canvas font-bold py-3.5 px-6 rounded-full text-center shadow transition-colors text-xs uppercase tracking-wider"
              >
                <i className="fa-regular fa-calendar-check text-brand-accent" aria-hidden="true" />
                <span>Bestil denne behandling</span>
              </a>
              <a
                href={`tel:${phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center justify-center gap-2 bg-canvas hover:bg-surface text-brand-charcoal font-semibold py-3.5 px-6 rounded-full text-center border border-brand-border transition-colors text-xs uppercase tracking-wider whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-brand-accent" aria-hidden="true" />
                <span>Ring {phone}</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 sm:py-24 bg-canvas border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left: Article / Lexical Content */}
            <div className="lg:col-span-8 space-y-12">
              
              {/* Featured Image */}
              {service.image?.[0] && (
                <div className="inline-block max-w-full">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${service.image[0].replace('https://assets.auxodev.dk/', '')}`}
                    alt={service.title}
                    className="w-auto h-auto max-h-[250px] rounded-3xl object-cover border border-brand-border shadow-xs max-w-full"
                  />
                </div>
              )}

              {/* Rich HTML Content */}
              <div 
                className="prose prose-stone prose-lg max-w-none prose-headings:font-serif prose-headings:text-brand-charcoal prose-a:text-brand-accent prose-strong:text-brand-charcoal prose-li:text-brand-muted"
                dangerouslySetInnerHTML={{ __html: service.content }}
              />

              {/* Connected Dynamic Gallery via slug reference */}
              {gallery && (
                <div className="pt-8 border-t border-brand-border space-y-6">
                  <div className="space-y-1">
                    <span className="text-xs uppercase font-bold tracking-widest text-brand-muted">
                      Billeder fra praksis
                    </span>
                    <h3 className="font-serif text-2xl font-bold text-brand-charcoal">
                      {gallery.title || "Galleri for behandlingen"}
                    </h3>
                    {gallery.description && (
                      <p className="text-brand-muted text-xs sm:text-sm">
                        {gallery.description}
                      </p>
                    )}
                  </div>

                  <GalleryLightbox gallery={gallery} />
                </div>
              )}

              {/* Direct Booking Section */}
              <div id="booking" className="pt-10 border-t border-brand-border">
                <div className="mb-6 space-y-2">
                  <span className="text-xs uppercase font-bold tracking-widest text-brand-muted">
                    Bestil Hjemmebehandling
                  </span>
                  <h3 className="font-serif text-2xl font-bold text-brand-charcoal">
                    Bestil tid til {service.title}
                  </h3>
                  <p className="text-brand-muted text-xs sm:text-sm">
                    Udfyld formularen, så vender vi hurtigt tilbage med forslag til tidspunkt i dit område.
                  </p>
                </div>

                <ContactForm initialService={service.title} />
              </div>

            </div>

            {/* Right: Sidebar with other services & trust notes */}
            <div className="lg:col-span-4 space-y-8">
              
              {/* Box 1: Quick info summary */}
              <div className="bg-surface/50 rounded-3xl p-6 border border-brand-border space-y-4 shadow-xs">
                <h4 className="font-serif text-lg font-bold text-brand-charcoal">
                  Behandlingsoversigt
                </h4>
                <ul className="space-y-3 text-xs text-brand-charcoal">
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-house-medical text-brand-accent mt-0.5" aria-hidden="true" />
                    <span><strong>Udføres i eget hjem</strong> på hele Sjælland (minus øerne)</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-regular fa-clock text-brand-accent mt-0.5" aria-hidden="true" />
                    <span><strong>Åbningstid:</strong> Mandag – Lørdag kl. 9 – 18</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-shield-check text-brand-accent mt-0.5" aria-hidden="true" />
                    <span><strong>Erfaring:</strong> Over 15 år og 8.000+ behandlede fødder</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-receipt text-brand-accent mt-0.5" aria-hidden="true" />
                    <span><strong>Helbredstillæg:</strong> Husk at oplyse hvis du er berettiget</span>
                  </li>
                </ul>
              </div>

              {/* Box 2: Other Services */}
              <div className="bg-surface/50 rounded-3xl p-6 border border-brand-border shadow-xs space-y-4">
                <h4 className="font-serif text-lg font-bold text-brand-charcoal">
                  Øvrige Behandlinger
                </h4>
                <div className="space-y-2">
                  {allServices
                    .filter((s) => s.id !== service.id && s.title !== service.title)
                    .map((other) => (
                      <Link
                        key={other.id || other.title}
                        to={`/ydelser/${other.slug?.da || other.slug || (other.title === 'Komplet Fodbehandling' ? 'komplet-fodbehandling' : other.title.toLowerCase().replace(/\s+/g, '-'))}`}
                        className="block p-3 rounded-xl hover:bg-surface transition-colors border border-transparent hover:border-brand-border"
                      >
                        <div className="font-bold text-xs text-brand-charcoal">{other.title}</div>
                        {other.price && (
                          <div className="text-[11px] text-brand-accent font-semibold">{other.price}</div>
                        )}
                      </Link>
                    ))}
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>
    </>
  );
}
